// Lightweight markdown renderer used by the public blog and the admin preview.
// Supports: # h1, ## h2, ### h3, > quote, - lists, **bold**, *italic*, `code`,
// [text](url) links, --- horizontal rule, paragraphs.
const escapeHtml = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

export const renderMarkdown = (md) => {
  if (!md) return "";

  // Split into lines so we can handle block-level constructs cleanly
  const lines = md.split("\n");
  const out = [];
  let i = 0;
  let inList = false;
  let inQuote = false;

  const closeList = () => { if (inList) { out.push("</ul>"); inList = false; } };
  const closeQuote = () => { if (inQuote) { out.push("</blockquote>"); inQuote = false; } };

  const inline = (raw) => {
    let s = escapeHtml(raw);
    // links [text](url) — url may not contain ) or whitespace
    s = s.replace(/\[([^\]]+)\]\((\S+?)\)/g, (_, text, href) => {
      const safeHref = href.replace(/"/g, "&quot;");
      const external = /^https?:\/\//i.test(href);
      const target = external ? ' target="_blank" rel="noopener noreferrer"' : "";
      return `<a href="${safeHref}"${target}>${text}</a>`;
    });
    s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/\*(.+?)\*/g, "<em>$1</em>");
    s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
    return s;
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Horizontal rule
    if (/^---+$/.test(trimmed)) {
      closeList(); closeQuote();
      out.push("<hr/>");
      i++;
      continue;
    }
    // Headings
    const h = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (h) {
      closeList(); closeQuote();
      const level = h[1].length;
      out.push(`<h${level}>${inline(h[2])}</h${level}>`);
      i++;
      continue;
    }
    // Blockquote
    if (trimmed.startsWith("> ")) {
      closeList();
      if (!inQuote) { out.push("<blockquote>"); inQuote = true; }
      out.push(`<p>${inline(trimmed.slice(2))}</p>`);
      i++;
      continue;
    }
    closeQuote();
    // List item
    if (/^[-*]\s+/.test(trimmed)) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${inline(trimmed.replace(/^[-*]\s+/, ""))}</li>`);
      i++;
      continue;
    }
    closeList();
    // Empty line → paragraph break (handled implicitly)
    if (!trimmed) { i++; continue; }
    // Paragraph — collect contiguous non-empty lines
    const buf = [trimmed];
    i++;
    while (i < lines.length && lines[i].trim() && !/^(#{1,3}\s|>\s|[-*]\s|---+$)/.test(lines[i].trim())) {
      buf.push(lines[i].trim());
      i++;
    }
    out.push(`<p>${inline(buf.join(" "))}</p>`);
  }
  closeList(); closeQuote();
  return out.join("\n");
};
